import { NavLink, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef, type CSSProperties } from 'react'
import clsx from 'clsx'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useAuth } from '@/hooks/useAuth'
import { canViewSidebarItem } from '@/config/routePermissions'
import { type PermissionType } from '@/types/auth'
import { NavShell, ThemeToggle, type NavHub, type RecentEntry } from '@profylr/ui'
import { useSidebarTheme } from '@/contexts/sidebar-theme-context'
import { useOrbAnimationCycle } from '@/hooks/useOrbAnimationCycle'
import UserMenu from '@/components/user/UserMenu'
import { useRecentRoutes } from '@/hooks/useRecentRoutes'
import { ResilienceIntelligenceOrb } from '@/assets/ResilienceIntelligenceOrb'
/*
 * Only the icons the navigation actually renders. Twenty-eight more were
 * imported here and used nowhere -- leftovers of sections deleted over the
 * last two years -- and one of them was `Sparkles`. Journeys 1.6 makes that
 * glyph mean "a model was involved", carried by `AiMarker` and its word; a
 * loose import of it in the navigation file is exactly the drift the icon
 * policy (1.9) exists to stop, so the whole dead set goes with it.
 */
import {
  Home,
  Building2,
  FlaskConical,
  AlertTriangle,
  Flame,
  ShieldCheck,
  FileText,
  BarChart3,
  Users,
  Wrench,
  SlidersHorizontal,
  Upload,
  Layers,
  BookOpen,
  Gauge,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

interface SidebarProps {
  isMobileOpen?: boolean
  onMobileClose?: () => void
}

type FeatureFlagKey = 'consumerDutyIntegration' | 'aiAnalytics' | 'advancedReporting'

/**
 * Routes that should keep the "Operational Components" sidebar entry
 * highlighted. These are the detail / sub-routes for each component type
 * surfaced on the Operational Components tabbed page. Keeping the highlight
 * across these routes avoids the sidebar losing context when the user
 * navigates into a single record from a list view.
 */
const OPERATIONAL_COMPONENT_DETAIL_PREFIXES = [
  '/performance-hub/operational-components',
  '/microservices',
  '/systems-registry',
  '/infrastructure-registry',
  '/locations-registry',
  '/teams',
  '/teams-registry',
  '/suppliers-registry',
  '/critical-data',
  '/relationships',
]

// eslint-disable-next-line react-refresh/only-export-components -- exported for sidebar regression tests
export function isOperationalComponentsRoute(pathname: string): boolean {
  return OPERATIONAL_COMPONENT_DETAIL_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  )
}

/**
 * Routes that should keep the "IBS Dashboard" sidebar entry highlighted.
 * Includes the dashboard itself plus all single-IBS detail routes (overview,
 * flows, microservices) which sit under /ibs/:hubIbsId rather than nesting
 * beneath /performance-hub/ibs.
 *
 * The IBS-create / IBS-continue / flow-builder wizards are deliberately
 * excluded — they are launched from the Configure IBSs page and remain
 * anchored to that sidebar entry (see isConfigureIbsRoute).
 */
const IBS_DASHBOARD_DETAIL_PREFIXES = ['/performance-hub/ibs', '/ibs']

// Routes that should keep the "Configure IBSs" sidebar entry highlighted
// when the user is inside a creation / continue wizard launched from the
// Configure IBSs page. Covers the top-level IBS wizards (/ibs/:id/edit and
// /ibs/:id/continue) plus the nested E2E Flow Builder wizards which are
// reached via the Configure IBSs > E2E Flows tab.
const CONFIGURE_IBS_WIZARD_PATTERN =
  /^\/ibs\/[^/]+\/(edit|continue|flows\/create|flows\/[^/]+\/edit)$/

// eslint-disable-next-line react-refresh/only-export-components -- exported for sidebar regression tests
export function isIbsDashboardRoute(pathname: string): boolean {
  if (CONFIGURE_IBS_WIZARD_PATTERN.test(pathname)) return false
  return IBS_DASHBOARD_DETAIL_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- exported for sidebar regression tests
export function isConfigureIbsRoute(pathname: string): boolean {
  if (pathname === '/configure-hub/ibs' || pathname.startsWith('/configure-hub/ibs/')) {
    return true
  }
  return CONFIGURE_IBS_WIZARD_PATTERN.test(pathname)
}

export interface NavItem {
  name: string
  href?: string
  icon: LucideIcon
  featureFlag?: FeatureFlagKey
  children?: NavItem[]
  requireClientAdmin?: boolean
  hideIfClientAdmin?: boolean
  // OR-312: when set, the user must hold ALL listed permissions for the
  // item to be visible (AND semantics). Other items use the OR semantics of
  // canViewSidebarItem via SIDEBAR_PERMISSIONS.
  //
  // Its only consumer was the Configure Hub "Overview" link (both IBS edit
  // and Components edit), which journeys 2H removed with its page. The gate
  // itself is kept rather than deleted with its last caller: it is the only
  // AND-semantics the navigation has, and the next item that needs one
  // should not have to reinvent it. Flagged in the batch report.
  requireAllPermissions?: PermissionType[]
}

export interface NavSection {
  title: string
  items: NavItem[]
  /**
   * Short label for the 64px rail. "Performance Hub" does not fit and reads as
   * filler once the rail is the only place a section name appears; `title`
   * stays the canonical name for the panel heading and the command palette.
   */
  railLabel?: string
  /** The rail is icon-first, so every section needs one. */
  railIcon?: LucideIcon
  // When true the section header label is not rendered, so the section's
  // items appear as standalone top-level entries. Used for the Command
  // Centre, which sits above the Performance Hub as a single item without
  // a redundant group label (mirrors the Consumer Duty sidebar).
  hideTitle?: boolean
}

// eslint-disable-next-line react-refresh/only-export-components -- exported for OR-74 sidebar regression tests
export const navigationSections: NavSection[] = [
  {
    // Standalone top-level entry above the Performance Hub. The header is
    // suppressed (hideTitle) so it renders as a single "Command Centre"
    // item rather than a "Command Centre" group containing a lone
    // "Command Centre" link.
    title: 'Command Centre',
    railLabel: 'Command Centre',
    railIcon: Gauge,
    hideTitle: true,
    items: [
      {
        name: 'Command Centre',
        href: '/command-centre',
        icon: Gauge,
      },
    ],
  },
  {
    title: 'Performance Hub',
    railLabel: 'Performance',
    railIcon: BarChart3,
    items: [
      {
        name: 'Dashboard',
        href: '/',
        icon: Home,
      },
      {
        name: 'IBS Dashboard',
        href: '/performance-hub/ibs',
        icon: Building2,
      },
      {
        name: 'Operational Components',
        href: '/performance-hub/operational-components',
        icon: Layers,
      },
      {
        name: 'Data Submission',
        href: '/performance-hub/bulk-performance-upload',
        icon: Upload,
      },
    ],
  },
  {
    title: 'Testing Hub',
    railLabel: 'Testing',
    railIcon: FlaskConical,
    items: [
      {
        name: 'Scenario Wizard',
        href: '/scenarios/wizard',
        icon: Wrench,
      },
      {
        name: 'Scenario Library',
        href: '/scenarios/library',
        icon: FileText,
      },
      {
        name: 'CMORG Library',
        href: '/scenarios/cmorg-library',
        icon: ShieldCheck,
      },
      {
        name: 'Client Vault',
        href: '/knowledge-hub',
        icon: BookOpen,
      },
    ],
  },
  {
    /*
     * Journeys 2M -- the section the August meeting called "Intelligences".
     * It is kept (the evaluation is in the batch report) and its items are in
     * alphabetical order, which is the meeting's second ask.
     *
     * The first ask was "dropdowns". It is already satisfied, and by the
     * mechanism the app has rather than a second one: the rail entry IS the
     * disclosure control -- clicking "Risk Intelligence" opens this section's
     * pages in the overlay panel and clicking it again closes them. NavShell
     * states the rule in its own header ("no accordions, no dropdowns, no
     * disclosure chevrons") because a chevron inside the panel would be a
     * second way to open the same list. So: nothing new here, deliberately.
     */
    title: 'Risk Intelligence',
    railLabel: 'Risk Intelligence',
    railIcon: AlertTriangle,
    items: [
      {
        name: 'Incidents',
        href: '/incidents',
        icon: Flame,
      },
      {
        name: 'Risks',
        href: '/risks',
        icon: AlertTriangle,
      },
    ],
  },
  {
    /*
     * Journeys 2H: the "Overview" item that pointed at /admin is gone with
     * the page behind it. Everything it listed -- the registries, their
     * counts, the Add/Bulk-upload actions -- is on the two pages below, and
     * /admin now redirects to Configure Components (configureHubRedirects).
     */
    title: 'Configure Hub',
    railLabel: 'Configure',
    railIcon: SlidersHorizontal,
    items: [
      {
        name: 'Configure IBSs',
        href: '/configure-hub/ibs',
        icon: Building2,
      },
      {
        name: 'Configure Components',
        href: '/configure-hub/components',
        icon: SlidersHorizontal,
      },
    ],
  },
  {
    title: 'Admin',
    railLabel: 'Admin',
    railIcon: Users,
    items: [
      {
        name: 'User Management',
        href: '/client-admin/users',
        icon: Users,
      },
      {
        name: 'Role Management',
        href: '/client-admin/roles',
        icon: ShieldCheck,
      },
    ],
  },
]

export default function Sidebar({ isMobileOpen = false, onMobileClose }: SidebarProps) {
  const location = useLocation()
  const { flags } = useFeatureFlags()
  const { isClientAdmin, authUser } = useAuth()
  const userPermissions = authUser?.permissions || []
  const { chromeStyle } = useSidebarTheme()
  /*
   * Always collapsed on load.
   *
   * Deliberately not persisted. The panel floats over the page rather than
   * reflowing it, so leaving it open across a reload would mean every page
   * load starts with content hidden behind a panel the user opened once,
   * minutes ago, for something else. Opening it is one click.
   */
  const [isCollapsed, setIsCollapsed] = useState(true)

  const [flyoutItem, setFlyoutItem] = useState<string | null>(null)
  const [flyoutTop, setFlyoutTop] = useState(0)
  const { cycleToken: orbCycleToken, triggerCycle: triggerOrbCycle } = useOrbAnimationCycle()
  const flyoutTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    const expanded: string[] = []
    if (location.pathname.startsWith('/ibs')) {
      expanded.push('IBS')
    }
    if (location.pathname.startsWith('/admin')) {
      expanded.push('Admin')
    }
    return expanded
  })

  const [manuallyCollapsed, setManuallyCollapsed] = useState<string[]>([])

  useEffect(() => {
    if (location.pathname.startsWith('/ibs')
        && !expandedItems.includes('IBS') && !manuallyCollapsed.includes('IBS')) {
      setExpandedItems(prev => [...prev, 'IBS'])
    }
    if (location.pathname.startsWith('/admin') && !expandedItems.includes('Admin') && !manuallyCollapsed.includes('Admin')) {
      setExpandedItems(prev => [...prev, 'Admin'])
    }
  }, [location.pathname, expandedItems, manuallyCollapsed])

  // Close mobile sidebar on location change
  useEffect(() => {
    if (isMobileOpen && onMobileClose) {
      onMobileClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const isFeatureEnabled = (item: NavItem): boolean => {
    if (item.requireClientAdmin && !isClientAdmin) return false
    if (item.hideIfClientAdmin && isClientAdmin) return false
    // RBAC v1.2: Client admins have access to all features.
    // Other users are checked against their assigned role permissions.
    if (!isClientAdmin) {
      // OR-312: AND-style permission gate (must hold every listed perm).
      if (item.requireAllPermissions && item.requireAllPermissions.length > 0) {
        const hasAll = item.requireAllPermissions.every(p => userPermissions.includes(p))
        if (!hasAll) return false
      }
      // Existing OR-style gate via SIDEBAR_PERMISSIONS (at least one match).
      if (item.href && !canViewSidebarItem(item.href, userPermissions)) return false
    }
    // For parent items with children, hide if all children are hidden
    if (item.children && item.children.length > 0) {
      const hasVisibleChild = item.children.some(child => isFeatureEnabled(child))
      if (!hasVisibleChild) return false
    }
    if (!item.featureFlag) return true
    return flags[item.featureFlag] ?? false
  }

  const toggleItem = (itemName: string) => {
    const isExpanded = expandedItems.includes(itemName)
    if (isExpanded) {
      setExpandedItems(prev => prev.filter(s => s !== itemName))
      setManuallyCollapsed(prev => [...prev, itemName])
    } else {
      setExpandedItems(prev => [...prev, itemName])
      setManuallyCollapsed(prev => prev.filter(s => s !== itemName))
    }
  }

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed)
  }

  const isActive = (href: string) => {
    if (href === '/' || href === '/admin') return location.pathname === href
    if (href === '/performance-hub/operational-components') {
      return isOperationalComponentsRoute(location.pathname)
    }
    if (href === '/performance-hub/ibs') {
      return isIbsDashboardRoute(location.pathname)
    }
    if (href === '/configure-hub/ibs') {
      return isConfigureIbsRoute(location.pathname)
    }
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  /*
   * Everything below adapts OR's navigation data to NavShell's shape. The
   * component owns the layout, the keyboard model, the Recent cap and the
   * accessibility contract; this file owns which sections exist, who may see
   * them, and which route counts as active.
   */
  const visibleSections = navigationSections
    .map((section) => ({ ...section, items: section.items.filter(isFeatureEnabled) }))
    .filter((section) => section.items.length > 0)

  const hubs: NavHub[] = visibleSections.map((section) => {
    const RailIcon = section.railIcon ?? Home
    // A section with a single item is a destination in its own right, so the
    // rail links straight to it and the panel shows its empty state.
    const single = section.items.length === 1 ? section.items[0] : undefined

    return {
      id: section.title,
      label: section.railLabel ?? section.title,
      icon: <RailIcon />,
      href: single?.href,
      pages: single
        ? []
        : section.items
            .filter((item) => item.href)
            .map((item) => ({
              id: item.href!,
              label: item.name,
              icon: <item.icon />,
              href: item.href!,
            })),
    }
  })

  const activeHub =
    visibleSections.find((section) =>
      section.items.some((item) => item.href && isActive(item.href)),
    ) ?? visibleSections[0]

  const [selectedHubId, setSelectedHubId] = useState<string | null>(null)

  useEffect(() => {
    // A route change re-syncs the rail, discarding any manual hub override.
    setSelectedHubId(null)
  }, [location.pathname])

  const activeHubId = selectedHubId ?? activeHub?.title ?? hubs[0]?.id
  const activePage = activeHub?.items.find((item) => item.href && isActive(item.href))

  const { recents } = useRecentRoutes()
  const recent: RecentEntry[] = recents.map((entry) => ({
    id: entry.path,
    label: entry.label,
    parentLabel: entry.context,
    href: entry.path,
  }))

  return (
    /*
     * The chrome preset lands here rather than on the root element, so it
     * repaints the sidebar and nothing else. `display: contents` keeps the
     * wrapper out of layout entirely -- NavShell stays a direct participant in
     * whatever lays this component out -- while custom properties still
     * inherit through it to the rail, the panel and the rail footer.
     */
    <div style={{ display: 'contents', ...chromeStyle }}>
      <NavShell
      hubs={hubs}
      activeHubId={activeHubId}
      activePageId={activePage?.href}
      activeRecentId={location.pathname}
      recent={recent}
      collapsed={isCollapsed}
      onCollapsedChange={setIsCollapsed}
      onHubSelect={setSelectedHubId}
      brand={{
        name: 'Operational Resilience',
        logo: (
          <NavLink
            to="/"
            onMouseEnter={triggerOrbCycle}
            aria-label="Operational Resilience home"
            className="focus-ring flex size-12 items-center justify-center rounded-lg outline-none"
          >
            <ResilienceIntelligenceOrb
              size={52}
              periodMs={3000}
              cycleToken={orbCycleToken}
              showButton={false}
            />
          </NavLink>
        ),
      }}
      // NavShell is router-agnostic; react-router is supplied here.
      renderLink={({ href, ref, children, ...rest }) => (
        <NavLink to={href} ref={ref as React.Ref<HTMLAnchorElement>} {...rest}>
          {children}
        </NavLink>
      )}
      /*
       * Account controls, at the foot of the rail. They are not navigation
       * between pages, which is why they sit below the divider rather than
       * among the hubs -- and why they are out of the Sections landmark.
       */
      footer={
        <>
          <ThemeToggle />
          <UserMenu />
        </>
      }
      /*
       * Section 9.7: below 48rem both tiers sit off-canvas behind the menu
       * control, sliding in as a 16rem drawer; from 48rem the shell takes
       * layout width (the rail alone to 64rem, both tiers beyond) and the
       * component decides the rest.
       */
      className={clsx(
        'fixed z-20 transition-transform duration-base ease-move md:static',
        isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
      )}
      />
    </div>
  )
}
